// Packing List Form - Bulk Entry
import React, { useState } from 'react';
import ResponsiveModal from './ResponsiveModal';
import { Luggage, Plus, X } from 'lucide-react';

interface PackingListFormProps {
  onAdd: (item: { title: string; notes?: string; metadata?: any }) => void;
}

interface PackingItem {
  id: string;
  itemName: string;
  quantity: string;
}

export default function PackingListForm({ onAdd }: PackingListFormProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [listName, setListName] = useState('');
  const [items, setItems] = useState<PackingItem[]>([
    { id: '1', itemName: '', quantity: '' }
  ]);

  const handleAddRow = () => {
    if (items.length >= 30) return;
    setItems([...items, { id: Date.now().toString(), itemName: '', quantity: '' }]);
  };

  const handleRemoveRow = (id: string) => {
    if (items.length === 1) return;
    setItems(items.filter(item => item.id !== id));
  };

  const handleUpdateItem = (id: string, field: 'itemName' | 'quantity', value: string) => {
    setItems(items.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const validItems = items.filter(item => item.itemName.trim());
    
    if (!listName.trim()) {
      alert('Please name your packing list!');
      return;
    }
    if (validItems.length === 0) return;

    validItems.forEach(item => {
      onAdd({
        title: item.itemName.trim(),
        notes: item.quantity ? `Qty: ${item.quantity}` : undefined,
        metadata: {
          type: 'packing',
          listName: listName.trim(),
          quantity: item.quantity || null
        }
      });
    });

    setListName('');
    setItems([{ id: Date.now().toString(), itemName: '', quantity: '' }]);
    setIsOpen(false);
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="w-full md:w-auto px-4 py-3 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition-colors touch-manipulation"
      >
        <Luggage className="w-5 h-5" />
        Add Packing Items
      </button>

      <ResponsiveModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Add Packing List Items"
        maxWidth="2xl"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Packing List Name */}
          <div>
            <label className="block text-sm font-semibold text-slate-300 mb-2">
              Packing List Name *
            </label>
            <input
              type="text"
              value={listName}
              onChange={(e) => setListName(e.target.value)}
              placeholder="e.g., Hawaii Trip, Weekend Getaway, Business Travel"
              className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500 text-base"
              required
              autoFocus
            />
            <p className="text-xs text-slate-500 mt-1">Name this packing list to organize your travel items</p>
          </div>

          <p className="text-sm text-slate-400">
            Add up to 30 items at once. Fill in item names and optional quantities.
          </p>

          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
            {items.map((item, index) => (
              <div key={item.id} className="flex gap-2 items-start">
                <div className="flex-shrink-0 w-8 pt-3 text-sm text-slate-500 font-semibold">
                  {index + 1}.
                </div>

                <div className="flex-1">
                  <input
                    type="text"
                    value={item.itemName}
                    onChange={(e) => handleUpdateItem(item.id, 'itemName', e.target.value)}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500 text-base"
                    placeholder="Item name"
                    autoFocus={index === 0}
                  />
                </div>

                <div className="w-32">
                  <input
                    type="text"
                    value={item.quantity}
                    onChange={(e) => handleUpdateItem(item.id, 'quantity', e.target.value)}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500 text-base"
                    placeholder="Qty"
                  />
                </div>

                <button
                  type="button"
                  onClick={() => handleRemoveRow(item.id)}
                  disabled={items.length === 1}
                  className="flex-shrink-0 p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Remove item"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>

          {items.length < 30 && (
            <button
              type="button"
              onClick={handleAddRow}
              className="w-full py-2 px-4 bg-slate-700 hover:bg-slate-600 text-slate-300 font-medium rounded-lg flex items-center justify-center gap-2 transition-colors text-sm"
            >
              <Plus className="w-4 h-4" />
              Add Another Item ({items.length}/30)
            </button>
          )}

          <div className="text-sm text-slate-400 text-center">
            {items.filter(i => i.itemName.trim()).length} item(s) ready to add
          </div>

          <button
            type="submit"
            disabled={items.filter(i => i.itemName.trim()).length === 0}
            className="w-full py-4 px-6 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition-colors touch-manipulation text-lg md:text-base mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Luggage className="w-5 h-5" />
            Add {items.filter(i => i.itemName.trim()).length} Item(s) to Packing List
          </button>
        </form>
      </ResponsiveModal>
    </>
  );
}
